import { UploadFile } from 'antd/lib/upload/interface';
import * as yup from 'yup';
import { nanoid } from '@reduxjs/toolkit';
import { RcFile } from 'rc-upload/lib/interface';
import { isArray } from 'lodash';
import { TypeOfShape } from 'yup/lib/object';

import api from 'api';

export type FileModel = {
	s3Key: string;
	fileName: string;
};

export type ImageFileModel = {
	imageSrc?: string;
} & FileModel;

export const FileYupSchema = {
	fileName: yup.string(),
	s3Key: yup.string(),
};

export const GetUploadLink = api.getUploadLink.bind(api);
export const ValidateProductBuild = api.validateProductBuild.bind(api);

export const getFileExt = (fileName: string) => {
	if (!fileName) return '';

	const fileNameArray = fileName.split('.');

	return fileNameArray[fileNameArray.length - 1];
};

export const ValidateS3Keys = (files?: TypeOfShape<typeof FileYupSchema>[]) => {
	return !!files?.every((file) => !!file.s3Key);
};

export const getUploadedFilesByExt = (files: FileModel[], ext: string) => {
	return files.filter(
		(file) =>
			!!file.s3Key && // only uploaded files have s3Key
			getFileExt(file.fileName).toLowerCase() === ext.toLowerCase(),
	);
};

export const generateUploadFileList = (
	files: FileModel | FileModel[] | null | undefined, // BE should return the same structure for all files
): UploadFile[] => {
	if (!files) return [];

	files = isArray(files) ? files : [files];

	return files.map((file) => ({
		uid: nanoid(),
		size: 1,
		url: '',
		type: '',
		status: 'done',
		name: file.fileName,
		// should contain s3Key for edit mode
		response: {
			s3Key: file.s3Key,
		},
	}));
};

export const isFileFormatValid = (
	file: RcFile | File,
	acceptedFileFormat: string[],
) => {
	const fileType = `.${getFileExt(file.name)}`;

	return acceptedFileFormat.some(
		(format: string) => format.toLowerCase() === fileType.toLowerCase(),
	);
};

export const isFileSizeValid = (file: RcFile, acceptedFileSize: number) =>
	file.size <= 1024 * 1024 * acceptedFileSize; // acceptedFileSize in Mb

export const downloadFileByObjectURL = async (
	objectURL: string,
	fileName: string = '',
) => {
	const link = document.createElement('a');

	link.href = objectURL;
	link.download = fileName;
	link.target = '_blank';

	// mozilla requires link to be in the dom for downloading
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

export interface Resolution {
	width: number;
	height: number;
}

export interface ImageResolution extends Resolution {}

export interface VideoResolution extends Resolution {}

export const isResolutionValid = (
	fileResolution: ImageResolution | null,
	acceptedResolution: ImageResolution,
) => {
	if (!fileResolution) {
		// when we can't get resolution consider image as valid
		// BE should have an additional validation for this case
		return true;
	}

	return (
		fileResolution.width <= acceptedResolution.width &&
		fileResolution.height <= acceptedResolution.height
	);
};

export const isExactResolutionValid = (
	fileResolution: ImageResolution | null,
	exactResolution: ImageResolution,
) => {
	if (!fileResolution) {
		return true;
	}

	return (
		fileResolution.width == exactResolution.width &&
		fileResolution.height == exactResolution.height
	);
};

export const isVideoExactResolutionValid = async (
	file: File,
	exactResolution: VideoResolution,
) => {
	const fileResolution = await getVideoResolution(file);

	return isExactResolutionValid(fileResolution, exactResolution);
};

export const getVideoResolution = (file: File): Promise<VideoResolution> => {
	return new Promise((resolve, reject) => {
		const video = document.createElement('video');
		video.preload = 'metadata';
		video.onloadedmetadata = function () {
			window.URL.revokeObjectURL(video.src);
			const width = video.videoWidth;
			const height = video.videoHeight;
			resolve({ height: height, width: width });
		};
		video.onerror = function () {
			reject('error');
		};
		video.src = URL.createObjectURL(file);
	});
};
